import { PeriodReportData } from './PeriodReportData'
import useProjectId from '../../customHooks/useProjectId'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAccessToken } from '../useAccessToken'
import { useCallback } from 'react'
import { makeResultFetch } from '../genericFetch'
import { apiRequestParams } from '../fetchOptions'
import { useHandleRetry } from '../useHandleRetry'

const useFetch = () => {
  const getAccessToken = useAccessToken()
  const projectId = useProjectId()

  const queryFunction = async () => {
    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const fetchedData = makeResultFetch<PeriodReportData[]>(
        `/Reports/${projectId}/ProjectForecast`,
        apiRequestParams(accessToken, 'GET')
      ).then((response) => {
        const { data, message, success } = response

        if (!success) throw Error(message)

        return data
      })

      return fetchedData
    })
  }

  return { projectId, queryFunction }
}

export const usePrefetchPeriodReportSnapshots = () => {
  const { projectId, queryFunction } = useFetch()
  const queryClient = useQueryClient()

  const prefetchQuery = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: [projectId, 'periodReportSnapshots'],
      queryFn: queryFunction,
    })
  }, [projectId, queryClient, queryFunction])

  return prefetchQuery
}

export const usePeriodReportSnapshots = () => {
  const { projectId, queryFunction } = useFetch()
  const handleRetry = useHandleRetry()

  const query = useQuery({
    queryKey: [projectId, 'periodReportSnapshots'],
    queryFn: queryFunction,
    enabled: !!projectId && !!queryFunction,
    retry: handleRetry,
  })
  return query
}
